<template>
  <div
    v-if="!isChildFacet || filteredFacetItems.length"
    ref="facet"
    :class="{
      'facet--mobile': !desktop,
      'facet--desktop': desktop,
      'facet--open': open,
      'facet--open-right': openRight,
      'facet--has-selected-items': hasSelectedItems,
      'facet--color': facet.color,
      'facet--single-select': facet.singleSelect,
      'facet--multi-select': facet.multiSelect,
      'facet--brand': facet.brand,
      'facet--child-facet': isChildFacet,
    }"
    class="facet"
  >
    <div
      ref="facetHeader"
      class="f-header"
      tabindex="0"
      @click="toggleFacet"
      @keydown.enter="toggleFacet"
      @keydown.space.prevent="toggleFacet"
    >
      <fn-headline
        class="f-title"
        size="h4"
        type="div"
      >
        {{ facet.name }}
        <fn-icon
          v-if="desktop"
          icon="arrow-down"
          size="xxs"
        />
        <div
          v-if="!desktop"
          class="f-title-selected-values"
        >
          {{ selectedItemsString }}
        </div>
        <fn-icon
          v-if="!desktop"
          icon="arrow-right"
          size="xxs"
        />
      </fn-headline>
    </div>
    <div
      v-show="open"
      ref="facetContent"
      class="f-content"
    >
      <div
        v-if="!isChildFacet && !desktop"
        class="f-list-header"
      >
        <fn-headline
          class="f-list-title"
          size="h4"
          type="div"
        >
          {{ facet.name }}
        </fn-headline>
        <fn-link
          v-if="hasSelectedItems"
          class="f-delete"
          no-arrow
          @click="resetFacet"
          @keydown.enter="resetFacet"
          @keydown.space.prevent="resetFacet"
        >
          {{ $t('filter.delete') }}
        </fn-link>
      </div>

      <fn-facet-search
        v-if="!isChildFacet && (facet.brand || facet.values.length > 10)"
        v-model:value="search"
        :name="facet.name"
      />

      <fn-facet-item-list
        v-if="filteredFacetItems.length"
        ref="facetItemList"
        :brand="facet.brand"
        :code="facet.code"
        :color="facet.color"
        :is-brand-page="isBrandPage"
        :is-child-facet="isChildFacet"
        :items="filteredFacetItems"
        :multi-select="facet.multiSelect"
        :single-select="facet.singleSelect"
        @change="onChangeFacetItem"
      />

      <div
        v-else
        class="f--no-result"
      >
        {{ $t('filter.noResults') }}
      </div>

      <div
        v-if="childFacets.length"
        class="f-special"
      >
        <fn-facet
          v-for="childFacet in childFacets"
          :id="`child-facet-${childFacet.name}`"
          :key="childFacet.code"
          ref="childFacet"
          :desktop="desktop"
          :facet="childFacet"
          :is-brand-page="isBrandPage"
          :is-child-facet="true"
          @change="onChangeChildFacet"
        />
      </div>

      <div
        ref="facetFooter"
        class="f-footer"
      >
        <fn-link
          v-if="desktop && hasSelectedItems"
          class="f-delete"
          no-arrow
          @click="resetFacet"
          @keydown.enter="resetFacet"
          @keydown.space.prevent="resetFacet"
        >
          {{ $t('filter.delete') }}
        </fn-link>
        <span v-else />

        <fn-button
          v-if="hasFiltersChanged"
          id="facet-save"
          class="f-save"
          icon="plus"
          secondary
          @click="saveFacet"
          @keydown.enter="saveFacet"
          @keydown.space.prevent="saveFacet"
          @keydown.tab.exact="toggleFacet"
        >
          {{ $t('filter.save') }}
        </fn-button>
        <fn-button
          v-else
          id="facet-close"
          class="f-close"
          tertiary
          icon="cross"
          @click="toggleFacet"
          @keydown.enter="toggleFacet"
          @keydown.space.prevent="toggleFacet"
          @keydown.tab.exact="toggleFacet"
        >
          {{ $t('filter.close') }}
        </fn-button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { deepClone } from '@/utils';
import ProductBadges from '@/mixins/ProductBadges';
import { PRICE_RANGE, BADGES_NEW } from '@/utils/facetConstants';

export default defineComponent({
  mixins: [ProductBadges],
  props: {
    open: {
      type: Boolean,
    },
    facet: {
      type: Object,
      required: true,
    },
    childFacets: {
      type: Array,
      default: () => [],
    },
    desktop: {
      type: Boolean,
    },
    isBrandPage: {
      type: Boolean,
    },
    isChildFacet: {
      type: Boolean,
    },
  },
  emits: ['change', 'save', 'toggleFacet'],
  data() {
    return {
      openRight: false,
      search: '',
      localFacetValues: [],
      localChildFacets: [],
    };
  },
  computed: {
    hasSelectedItems() {
      const hasSelectedItemsLocal = !!this.selectedItems.length;
      const isPriceFacet = this.localFacet.code === PRICE_RANGE;
      let hasSelectedItemsChild = false;

      for (const childFacet of this.localChildFacets) {
        for (const value of childFacet.values) {
          const isNewProductFacet = value.code === BADGES_NEW;
          if (isNewProductFacet && isPriceFacet) {
            hasSelectedItemsChild = false;
          } else {
            hasSelectedItemsChild = !!value.selected;
          }
        }
      }

      // Check if subfacets are selected
      for (const facetValue of this.localFacetValues) {
        for (const value of facetValue.values || []) {
          if (value?.selected) {
            return true;
          }
        }
      }

      return hasSelectedItemsLocal || hasSelectedItemsChild;
    },

    hasFiltersChanged() {
      const hasFiltersChangedLocal = this.checkFacetValuesChanged(this.localFacetValues, this.facet.values);
      const hasFiltersChangedChild = this.checkFacetsChanged(this.localChildFacets, this.childFacets);

      return hasFiltersChangedLocal || hasFiltersChangedChild;
    },

    localFacet() {
      return {
        code: this.facet.code,
        values: this.localFacetValues,
      };
    },

    selectedItems() {
      return this.localFacetValues.filter((item) => item.selected === true);
    },

    selectedItemsString() {
      let string = '';

      for (let i = 0; i < this.selectedItems.length; i += 1) {
        string += `${this.$t(this.selectedItems[i].name)}, `;
      }

      return string.slice(0, -2);
    },

    filteredFacetItems() {
      let filteredItems = this.localFacetValues;

      // Filter by child facet configuration
      if (this.isChildFacet && this.facet?.facetItemWhitelist?.length) {
        const sanitizedFacetItemWhitelist = this.facet.facetItemWhitelist.filter((value) => !!value);
        if (sanitizedFacetItemWhitelist.length) {
          filteredItems = filteredItems.filter((facet) => this.facet.facetItemWhitelist.includes(facet.code));
        }
      }

      // Filter by search query string
      if (this.search) {
        filteredItems = filteredItems.filter((facet) => facet.name.toLowerCase().match(this.search.toLowerCase()));
      }

      return this.highlightFacetItemSearchMatch(filteredItems);
    },
  },
  watch: {
    facet(newFacet) {
      this.localFacetValues = deepClone(newFacet.values);
    },

    childFacets(newChildFacets) {
      this.localChildFacets = deepClone(newChildFacets);
    },
  },
  mounted() {
    // Deep clone facets objects to not mutate props directly
    this.localFacetValues = deepClone(this.facet.values);
    this.localChildFacets = deepClone(this.childFacets);
  },
  methods: {
    resetFacet() {
      // Reset local facet
      for (let i = 0; i < this.localFacetValues.length; i += 1) {
        this.localFacetValues[i].selected = false;

        // Reset Sub Facets
        if (this.localFacetValues[i].values?.length) {
          for (let j = 0; j < this.localFacetValues[i].values?.length; j += 1) {
            this.localFacetValues[i].values[j].selected = false;
          }
        }
      }

      // Reset child facets
      if (this.$refs?.childFacet?.length) {
        for (let i = 0; i < this.$refs.childFacet.length; i += 1) {
          this.$refs.childFacet[i].resetFacet();
        }
      }

      this.resetSearch();

      this.$emit('change', this.localFacet);
    },

    saveFacet(shouldToggleFacet = false) {
      if (this.hasFiltersChanged) {
        this.$emit('save', [this.localFacet, ...this.localChildFacets]);
      }

      if (shouldToggleFacet) {
        this.toggleFacet();
      }
    },

    toggleFacet() {
      this.resetSearch();
      this.setFacetPosition();
      this.$emit('toggleFacet', this.open ? null : this.facet.code);
    },

    resetSearch() {
      this.search = '';
    },

    setFacetPosition() {
      const facetRect = this.$refs.facet.getBoundingClientRect();
      this.openRight = window.innerWidth - facetRect.right + facetRect.width < 300;
    },

    /**
     * @param facetValues {Object[]}
     * @returns {Object[]}
     */
    highlightFacetItemSearchMatch(facetValues) {
      if (this.search) {
        const search = new RegExp(this.search, 'ig');

        for (let i = 0; i < facetValues.length; i += 1) {
          facetValues[i].searchTerm = facetValues[i].name.replace(search, (matchedStr) => `<b>${matchedStr}</b>`);
        }
      }

      return facetValues;
    },

    /**
     * @param facetsA {Object[]} - modified facets
     * @param facetsB {Object[]} - original facets
     * @returns {boolean}
     */
    checkFacetsChanged(facetsA, facetsB) {
      for (const facetA of facetsA) {
        for (const facetB of facetsB) {
          if (this.checkFacetValuesChanged(facetA.values, facetB.values)) {
            return true;
          }
        }
      }

      return false;
    },

    /**
     * @param valuesA {Object[]} - modified array
     * @param valuesB {Object[]} - original array
     * @returns {boolean}
     */
    checkFacetValuesChanged(valuesA, valuesB) {
      for (const valueA of valuesA) {
        for (const valueB of valuesB) {
          if (valueA.code === valueB.code && valueA.selected !== valueB.selected) {
            return true;
          }

          // checks if subfacets have changed as well
          const hasSubFacets = valueA?.values?.length && valueB?.values?.length;
          if (hasSubFacets) {
            for (const subValueB of valueB.values) {
              // return true if the code and selected value matches
              if (
                valueA.values.find((subValueA) => subValueA.code === subValueB.code)?.selected !== subValueB.selected
              ) {
                return true;
              }
            }
          }
        }
      }

      return false;
    },

    /**
     * @param code {String}
     * @param selected {Boolean}
     * @param isSubFacet {Boolean}
     * @param parentFacet {String}
     */
    onChangeFacetItem({ code, selected }, isSubFacet = false, parentFacet = null) {
      const facetCode = isSubFacet ? parentFacet : code;

      // Find the index of the facet value with the specified facet code
      const index = this.localFacetValues.findIndex((facetValue) => facetValue.code === facetCode);
      const localFacetValue = this.localFacetValues[index];

      // Update the selected value for the facet value
      if (!isSubFacet) {
        localFacetValue.selected = selected;
      } else {
        const subIndex = localFacetValue.values.findIndex((facetValue) => facetValue.code === code);
        localFacetValue.values[subIndex].selected = selected;
      }

      this.$emit('change', this.localFacet);
    },

    /**
     * @param childFacet {Object}
     */
    onChangeChildFacet(childFacet) {
      const childFacetIndex = this.localChildFacets.findIndex((facet) => facet.code === childFacet.code);
      this.localChildFacets[childFacetIndex] = childFacet;
    },
  },
});
</script>

<style lang="scss" scoped>
.facet {
  display: flex;
  flex-direction: column;

  &.facet--mobile {
    .f-header {
      .f-title {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;

        .f-title-selected-values {
          flex-grow: 1;
          text-align: right;
          margin-left: $space;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          color: palette(grey);
          font-weight: normal;
          font-size: map-get($font-sizes, 'm');
        }

        .icon {
          margin-left: $space;
          font-size: map-get($font-sizes, 'm');
        }
      }
    }

    .f-content {
      opacity: 0;
    }

    &.facet--open {
      .f-content {
        left: 0;
        opacity: 1;
      }
    }
  }

  .f-header {
    position: relative;
    flex: 1 1 auto;
    padding: 0 $grid-margin-width;
    border-bottom: 1px solid palette(sand);

    .f-title {
      flex: 1 0 auto;
      display: block;
      position: relative;
      padding: $pad 0;
    }
  }

  .f-content {
    flex: 1 1 100%;
    display: flex;
    flex-direction: column;
    overflow-y: hidden;
    width: 100%;
    position: absolute;
    z-index: 1;
    left: 102%;
    top: 0;
    bottom: 0;
    background: $color-general-background-neutral-default;
    transition: all 0.2s ease-in-out;

    .f--no-result {
      padding: 8px 32px 8px 16px;
      font-size: map-get($font-sizes, 'm');
      font-weight: bold;
    }

    .f-list-header {
      border-bottom: 1px solid palette(sand);
      padding: 4px 0 18px;
      text-align: center;
      line-height: 26px;

      .f-list-title {
        margin-bottom: 0;
      }

      .f-delete {
        position: absolute;
        top: -12px;
        right: 0;
        flex: 0 1 auto;
        padding: $pad 0;
        color: $color-general-text-brand-default;
      }
    }

    .f-search {
      border-bottom-color: palette(sand);
    }

    .f-footer {
      margin-top: auto;
      padding-top: $pad;

      .f-save,
      .f-close {
        width: 100%;

        :deep(.icon) {
          font-size: map-get($font-sizes, 'l');
        }
      }

      .f-delete {
        color: $color-general-text-brand-default;
      }
    }
  }

  .f-list {
    margin: 0;
  }

  &.facet--desktop {
    position: relative;
    display: inline-block;

    .f-header {
      padding: 0;
      border: 1px solid palette(grey, light);
      border-radius: map-get($border-radiuses, 'l');
      cursor: pointer;
      user-select: none;

      .f-title {
        font-size: map-get($font-sizes, 'm');
        padding: math.div($pad, 3) math.div($pad, 2);
        font-weight: normal;

        .icon {
          display: inline-block;
          vertical-align: middle;
          line-height: 1;
          margin-left: math.div($pad, 3);
        }
      }
    }

    .f-content {
      display: none;
      position: absolute;
      top: auto;
      bottom: auto;
      right: auto;
      left: 0;
      z-index: 4;
      width: 300px;
      margin-top: math.div($grid-margin-width, 2);
      border: 1px solid palette(grey, light);
      border-radius: map-get($border-radiuses, 'l');
      box-shadow: 0 5px 5px 0 rgba(palette(black), 0.2);
      background: $color-general-background-neutral-default;
    }

    .f-list {
      max-height: 7 * 40px;
      padding: $pad 0 10px;
    }

    .f-footer {
      display: flex;
      justify-content: flex-end;
      gap: $space;
      border-top: 1px solid palette(grey, light);
      padding: math.div($pad, 2);

      .f-close,
      .f-save {
        width: auto;
      }

      .f-delete {
        font-weight: bold;
        padding: 0 math.div($pad, 2);
      }
    }

    &.facet--has-selected-items {
      .f-header {
        border-color: palette(black);
      }
    }

    &.facet--open {
      .f-header {
        border-color: transparent;
      }

      .f-content {
        display: flex;
      }

      &.facet--desktop {
        .f-header {
          .f-title {
            .icon {
              transform: rotate(180deg);
            }
          }
        }

        &.facet--open-right {
          .f-content {
            left: auto;
            right: 0;
          }
        }
      }
    }
  }

  &.facet--child-facet {
    width: 100%;
    border-top: 1px solid palette(sand, base);

    .f-footer,
    .f-header {
      display: none;
    }

    .f-content {
      position: relative;
      width: 100%;
      margin: 0;
      border: none;
      box-shadow: none;
    }

    :deep(.f-list-item-label) {
      &:hover {
        background: transparent;
      }
    }

    &.facet--desktop {
      border-top-color: palette(grey, light);
    }
  }
}
</style>
